import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

// Searchable multi-select with chips.
//
// Markup:
//   <div data-controller="multi-select" data-multi-select-name-value="filter[criteria][module_ids][]">
//     <div data-multi-select-target="chips" class="..."></div>
//     <input type="text" data-multi-select-target="search" data-action="input->multi-select#filter focus->multi-select#openDropdown" />
//     <div data-multi-select-target="dropdown" hidden>
//       <button type="button" data-option-id="1" data-option-label="Alpha" data-action="click->multi-select#toggle">Alpha</button>
//       ...
//     </div>
//     <div data-multi-select-target="hidden"></div>
//   </div>
//
// Initial selection passed via data-multi-select-selected-value='[1,2]'.
export default class extends Controller {
  static targets = ["chips", "search", "dropdown", "hidden", "option"]
  static values = { name: String, selected: Array, placeholder: String }

  connect() {
    this.selectedIds = new Set((this.selectedValue || []).map(String))
    this.render()
    this.boundDocClick = this.handleDocClick.bind(this)
    document.addEventListener("click", this.boundDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocClick)
  }

  handleDocClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  openDropdown() {
    if (this.hasDropdownTarget) this.dropdownTarget.hidden = false
  }

  closeDropdown() {
    if (this.hasDropdownTarget) this.dropdownTarget.hidden = true
  }

  filter(event) {
    const term = (event.target.value || "").trim().toLowerCase()
    this.optionTargets.forEach((opt) => {
      const label = (opt.dataset.optionLabel || opt.textContent || "").toLowerCase()
      opt.hidden = term.length > 0 && !label.includes(term)
    })
    this.openDropdown()
  }

  toggle(event) {
    event.preventDefault()
    const id = String(event.currentTarget.dataset.optionId)
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id)
    } else {
      this.selectedIds.add(id)
    }
    this.render()
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
    this.optionTargets.forEach((opt) => { opt.hidden = false })
  }

  removeChip(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = String(event.currentTarget.dataset.optionId)
    this.selectedIds.delete(id)
    this.render()
  }

  render() {
    // Update chips
    if (this.hasChipsTarget) {
      this.chipsTarget.innerHTML = ""
      if (this.selectedIds.size === 0) {
        const placeholder = document.createElement("span")
        placeholder.className = "text-xs text-gray-400"
        placeholder.textContent = this.placeholderValue || i18n.t("js.multi_select.none_selected")
        this.chipsTarget.appendChild(placeholder)
      } else {
        this.optionTargets.forEach((opt) => {
          const id = String(opt.dataset.optionId)
          if (!this.selectedIds.has(id)) return
          const label = opt.dataset.optionLabel || opt.textContent.trim()
          const chip = document.createElement("span")
          chip.className = "inline-flex items-center gap-1 rounded bg-brand-100 px-2 py-0.5 text-xs text-brand-700"
          chip.innerHTML = `${this.escape(label)} <button type="button" class="ml-1 text-brand-700 hover:text-red-600" data-option-id="${this.escape(id)}" data-action="click->multi-select#removeChip">×</button>`
          this.chipsTarget.appendChild(chip)
        })
      }
    }

    // Update checkmarks on options
    this.optionTargets.forEach((opt) => {
      const id = String(opt.dataset.optionId)
      const checked = this.selectedIds.has(id)
      opt.dataset.selected = checked ? "true" : "false"
      opt.classList.toggle("bg-brand-50", checked)
      opt.classList.toggle("font-medium", checked)
    })

    // Rebuild hidden inputs
    if (this.hasHiddenTarget) {
      this.hiddenTarget.innerHTML = ""
      if (this.selectedIds.size === 0) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = this.nameValue
        input.value = ""
        this.hiddenTarget.appendChild(input)
      } else {
        this.selectedIds.forEach((id) => {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = this.nameValue
          input.value = id
          this.hiddenTarget.appendChild(input)
        })
      }
    }
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
